import * as React from 'react'
import { Link } from 'react-router-dom'
import { authService } from '@/services/AuthService'
import { requestErrorMessage } from '@/helpers/apiClient'
import { requestPasswordResetInputSchema } from '@lib/input/user.input'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import { AuthShell } from '@/components/AuthShell'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { FormError } from '@/components/ui/form-error'

/** Public "forgot password" page: request a reset email. Always confirms without
 * revealing whether the address maps to an account. */
export function ForgotPasswordPage() {
  const [email, setEmail] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const [sent, setSent] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setError(null)
    const parsed = requestPasswordResetInputSchema.safeParse({ email })
    if (!parsed.success) {
      setError('Enter a valid email address.')
      return
    }
    setSubmitting(true)
    try {
      await authService.requestPasswordReset(parsed.data)
      setSent(true)
    } catch (err) {
      setError(requestErrorMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthShell>
      <Card size="lg" className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="text-display">Reset password</CardTitle>
          <CardDescription>
            {sent ? 'Check your inbox.' : 'Enter your email and we’ll send you a reset link.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sent ? (
            <div className="space-y-4">
              <p className="text-sm text-ink-muted">
                If an account exists for <span className="font-medium text-ink">{email}</span>, a password-reset
                link is on its way. The link expires in 1 hour.
              </p>
              <Link to="/login" className="text-sm underline">
                Back to sign in
              </Link>
            </div>
          ) : (
            <form className="space-y-4" onSubmit={handleSubmit} noValidate>
              <Field label="Email" htmlFor="email">
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Field>
              {error ? <FormError>{error}</FormError> : null}
              <Button pending={submitting} type="submit" className="w-full">
                {submitting ? 'Sending…' : 'Send reset link'}
              </Button>
              <div className="text-center">
                <Link to="/login" className="text-sm text-ink-muted underline">
                  Back to sign in
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </AuthShell>
  )
}
